import React, { useState } from "react";
import { FiKey } from "react-icons/fi";
import PoleFormularza from "../common/PoleFormularza";
import { apiService } from "../../api/apiService";
import { DLUGOSC_PINU, poprawnyNowyPin } from "./dlugoscPinu";
import s from "./PinNaZadanie.module.css";

/**
 * ZMIANA WŁASNEGO PIN-U ADMINISTRATORA NA 5 CYFR — jeden formularz dla bramki strony (AdminPinGate) i okna PIN-u
 * na żądanie (PinNaZadanie).
 *
 * [PIN-ZARZAD, decyzja właściciela 2026-09-27] „pin zostaw tylko dla admina 5 cyfry to jest ok". Stary, 4-cyfrowy PIN
 * działa do zmiany; po jego poprawnym podaniu serwer odpowiada `wymagaZmiany`, a ekran pokazuje TEN formularz.
 * „Później" przepuszcza do pracy (bez blokowania) — prośba wróci przy następnym podaniu starego PIN-u.
 * Zapis: `PUT /health/admin-pin` (ranga administratora + ciasteczko PIN-u + OBECNY PIN w treści żądania).
 */
/* [PIN-ZARZAD poprawka 2026-09-28, uwaga przeglądu] Serwer wymaga przy zmianie OBECNEGO PIN-u (samo ciasteczko zostawionej
   otwartej sesji nie wystarczy). Bramka i okno PIN-u przekazują ten, który człowiek podał chwilę wcześniej (`obecnyPin`);
   gdy go nie ma, formularz pokazuje pole „Obecny PIN”. */
/* [PIN-ZARZAD poprawka 2 2026-09-28, uwagi przeglądu]
   • `rozruch` (PIN otworzył PIN SYSTEMOWY, nikt nie ma własnego): BYŁO — zapis zakładał PIERWSZY PIN indywidualny, więc
     każdy inny administrator tracił wejście do sekcji za PIN-em do czasu nadania mu PIN-u (jedno kliknięcie w karcie, która
     wyglądała jak zwykła wymiana). TERAZ formularz WYMIENIA PIN SYSTEMOWY (`pinSystemowy`) — ten, którym człowiek wszedł;
     pozostali wchodzą dalej, nowym PIN-em systemowym;
   • `onPozniej` jest opcjonalne — bez niego (szuflada „PIN administratora” własnego konta) nie ma przycisku „Później”;
   • `obecnyWymagany={false}` — pole „Obecny PIN” zostaje, ale puste nie blokuje (konto bez żadnego PIN-u w systemie);
     odmowę serwera o obecny PIN pokazujemy POD tym polem. */
const ZmianaPinuAdministratora = ({ komunikat = null, rozruch = false, obecnyPin = "", obecnyWymagany = true, onZapisano, onPozniej }) => {
    const [obecny, setObecny] = useState("");
    const [bladObecnego, setBladObecnego] = useState(null);
    const [nowy, setNowy] = useState("");
    const [powtorz, setPowtorz] = useState("");
    const [blad, setBlad] = useState(null);
    const [bladPowtorzenia, setBladPowtorzenia] = useState(null);
    const [trwa, setTrwa] = useState(false);

    const cyfry = (v) => String(v || "").replace(/\D/g, "").slice(0, DLUGOSC_PINU);

    const zapisz = async (e) => {
        if (e && typeof e.preventDefault === "function") e.preventDefault();
        if (trwa) return;
        if (!poprawnyNowyPin(nowy)) {
            setBlad(`PIN musi mieć dokładnie ${DLUGOSC_PINU} cyfr.`);
            return;
        }
        if (nowy !== powtorz) {
            setBladPowtorzenia("Oba pola muszą mieć ten sam PIN.");
            return;
        }
        const obecnyDoWyslania = obecnyPin || obecny;
        if (!obecnyDoWyslania && obecnyWymagany) {
            setBladObecnego("Podaj obecny PIN — bez niego PIN-u nie zmienisz.");
            return;
        }
        setTrwa(true);
        setBlad(null);
        try {
            const res = rozruch
                ? await apiService.zmienWlasnyPinAdministratora(nowy, obecnyDoWyslania, { pinSystemowy: true })
                : await apiService.zmienWlasnyPinAdministratora(nowy, obecnyDoWyslania);
            // Szuflada własnego konta zostaje na ekranie — po zapisie pola są puste (PIN nie wisi w formularzu).
            setObecny("");
            setNowy("");
            setPowtorz("");
            if (typeof onZapisano === "function") onZapisano(res?.data?.message || "Zapisano nowy, 5-cyfrowy PIN administratora.");
        } catch (err) {
            const dane = err?.response?.data;
            const tresc = dane?.message || "Nie udało się zapisać nowego PIN-u. Spróbuj ponownie za chwilę.";
            if (String(dane?.code || "").startsWith("obecny_pin") && !obecnyPin) setBladObecnego(tresc);
            else setBlad(tresc);
        } finally {
            setTrwa(false);
        }
    };

    return (
        <form className={s.forma} onSubmit={zapisz} data-testid="zmiana-pinu-administratora">
            <p className={s.opis}>
                <FiKey aria-hidden="true" className={s.ikona} />
                <span>
                    {komunikat || "Twój PIN administratora ma 4 cyfry. Od 27.09.2026 PIN ma 5 cyfr — ustaw nowy. Stary działa do czasu zmiany."}
                    {typeof onPozniej === "function" && " Nowy PIN podajesz od razu przy następnym wejściu; „Później” pozwala dokończyć pracę starym PIN-em."}
                    {/* W rozruchu (tylko PIN systemowy) zapis wymienia PIN SYSTEMOWY — mówimy o skutku przed kliknięciem. */}
                    {rozruch && " Wszedłeś PIN-em systemowym (wspólnym dla administratorów bez własnego PIN-u) — zapis wymieni PIN SYSTEMOWY: stary przestanie działać, a pozostali administratorzy wejdą nowym. Przekaż go im albo nadaj im PIN-y indywidualne."}
                </span>
            </p>
            {!obecnyPin && (
                <PoleFormularza
                    etykieta="Obecny PIN"
                    blad={bladObecnego}
                    id="zmiana-pinu-obecny"
                    podpowiedz={obecnyWymagany ? undefined : "Własny albo systemowy — jeśli w systemie jest ustawiony jakikolwiek PIN."}
                >
                    <input
                        type="password"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={DLUGOSC_PINU}
                        value={obecny}
                        onChange={(ev) => { setBladObecnego(null); setObecny(cyfry(ev.target.value)); }}
                        className={s.pole}
                    />
                </PoleFormularza>
            )}
            <PoleFormularza etykieta={`Nowy PIN (${DLUGOSC_PINU} cyfr)`} blad={blad} id="zmiana-pinu-nowy">
                <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={DLUGOSC_PINU}
                    value={nowy}
                    onChange={(ev) => { setBlad(null); setNowy(cyfry(ev.target.value)); }}
                    className={s.pole}
                />
            </PoleFormularza>
            <PoleFormularza etykieta="Powtórz nowy PIN" blad={bladPowtorzenia} id="zmiana-pinu-powtorz">
                <input
                    type="password"
                    inputMode="numeric"
                    autoComplete="new-password"
                    maxLength={DLUGOSC_PINU}
                    value={powtorz}
                    onChange={(ev) => { setBladPowtorzenia(null); setPowtorz(cyfry(ev.target.value)); }}
                    className={s.pole}
                />
            </PoleFormularza>
            <div className={s.stopka}>
                {typeof onPozniej === "function" && (
                    <button type="button" className={s.ghostBtn} onClick={() => onPozniej()}>
                        Później
                    </button>
                )}
                <button type="submit" className={s.primaryBtn} disabled={trwa || nowy.length !== DLUGOSC_PINU || powtorz.length !== DLUGOSC_PINU}>
                    {trwa ? "Zapisuję…" : "Zapisz nowy PIN"}
                </button>
            </div>
        </form>
    );
};

export default ZmianaPinuAdministratora;
